import { domAnimation, LazyMotion, MotionConfig } from 'framer-motion'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RouterProvider } from 'react-router-dom'

import { authApi } from '@/features/auth/api/authApi'
import { AuthBootSplash } from '@/features/auth/components/AuthBootSplash'
import { useLocaleSync } from '@/features/auth/hooks/useLocaleSync'
import { authBroadcast } from '@/features/auth/lib/authBroadcast'
import { refreshScheduler } from '@/features/auth/lib/refreshScheduler'
import { refreshTokenStore } from '@/features/auth/lib/refreshTokenStore'
import { useAuthStatus, useAuthStore } from '@/features/auth/store/authStore'
import { CookieBanner } from '@/features/consent/components/CookieBanner'
import { useConsentStore } from '@/features/consent/store/consentStore'
import { registerPush } from '@/features/push/pushRegistration'
import { WelcomeModal } from '@/features/welcome/components/WelcomeModal'
import { isNative } from '@/shared/api/platform'
import { BrutalToaster } from '@/shared/components/BrutalToaster'
import { UpdateRequiredScreen } from '@/shared/components/UpdateRequiredScreen'
import { useAndroidBackButton } from '@/shared/hooks/useAndroidBackButton'
import { useAppUpdateNotice } from '@/shared/hooks/useAppUpdateNotice'
import { useAppUrlOpen } from '@/shared/hooks/useAppUrlOpen'
import { initClarity } from '@/shared/lib/clarity'
import { initMetaPixel, revokeMetaPixel } from '@/shared/lib/metaPixel'
import { initNewRelic } from '@/shared/lib/newrelic'

import { router } from './router'

async function boot(): Promise<void> {
  const store = useAuthStore.getState()
  store.markBooting()
  // Nativo sin refresh token guardado → anónimo sin pegar al backend (evita un 401
  // inútil en el arranque en frío). Web sigue intentando el refresh por cookie.
  if (isNative() && !(await refreshTokenStore.get())) {
    store.markAnonymous()
    return
  }
  try {
    const refreshed = await authApi.refresh()
    const expiresAt = Date.parse(refreshed.accessTokenExpiresAt)
    store.setAccessToken(refreshed.accessToken, expiresAt)
    try {
      const me = await authApi.me()
      store.setAuthenticated(me, refreshed.accessToken, expiresAt)
      refreshScheduler.schedule()
      void registerPush() // native-only, no-op en web
    } catch {
      store.clear()
    }
  } catch {
    store.markAnonymous()
  }
}

export function App() {
  const status = useAuthStatus()
  const { i18n } = useTranslation()
  // Botón atrás nativo de Android → navega en la SPA (o doble-atrás para salir en
  // la raíz). No-op en web. Ver el hook.
  useAndroidBackButton()
  useAppUpdateNotice()
  // Enlaces de matchplay.es que abren la app → su pantalla. No-op en web. Ver el hook.
  useAppUrlOpen()
  // Suscripción al consent store: si el user acepta analíticas durante esta
  // sesión, arrancamos NR sin esperar al próximo refresh. initNewRelic() es
  // idempotente, así que llamarlo aunque ya esté inicializado es no-op.
  const analyticsConsent = useConsentStore((s) => s.decision?.analytics === true)
  useEffect(() => {
    if (analyticsConsent) {
      initNewRelic()
      initClarity()
    }
  }, [analyticsConsent])
  // Publicidad (píxel de Meta): aceptar lo carga; retirarlo desde "Gestionar
  // cookies" corta el envío en esta misma sesión (revokeMetaPixel es no-op si
  // nunca se cargó).
  const marketingConsent = useConsentStore((s) => s.decision?.marketing === true)
  useEffect(() => {
    if (marketingConsent) initMetaPixel()
    else revokeMetaPixel()
  }, [marketingConsent])

  // Sincroniza users.locale del backend con localeStore tras login/refresh.
  // Si userToggled, PATCHea el backend; si no, acepta el valor del backend.
  useLocaleSync()

  // Mantenemos <html lang> sincronizado con el idioma activo de i18next.
  // index.html lleva lang="es" hardcoded — sin este efecto, cuando el user
  // cambia a EN, lectores de pantalla y Google siguen viendo "es" (audit
  // SEO 2026-06-04, Sprint 2 subtarea 2).
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  useEffect(() => {
    void boot()
    const unsubscribe = authBroadcast.subscribe((msg) => {
      if (msg.type === 'logout') {
        useAuthStore.getState().clear()
        refreshScheduler.cancel()
      } else if (msg.type === 'login' || msg.type === 'refreshed') {
        void boot()
      }
    })
    return () => {
      unsubscribe()
      refreshScheduler.cancel()
    }
  }, [])

  // CookieBanner se renderiza fuera del switch de status para que aparezca
  // también durante el AuthBootSplash — RGPD exige consent ANTES de cargar
  // scripts no necesarios, y el splash puede durar unos cientos de ms.
  return (
    // LazyMotion: carga el set `domAnimation` de framer-motion (animaciones,
    // variants, exit y gestos) para que los componentes `m.*` de la app pesen
    // ~5 KB en vez de ~34 KB — importa para el webview nativo.
    // MotionConfig reducedMotion="user": respeto GLOBAL a prefers-reduced-motion
    // (framer omite las animaciones de transform; la opacidad se mantiene).
    <LazyMotion features={domAnimation}>
      <MotionConfig reducedMotion="user">
        {status === 'idle' || status === 'booting' ? (
          <AuthBootSplash />
        ) : (
          <>
            <RouterProvider router={router} />
            <BrutalToaster />
            {/* Modal de bienvenida de primera visita — solo tras el boot (no
                durante el splash). Se auto-oculta si ya se vio. */}
            <WelcomeModal />
          </>
        )}
        {/* CookieBanner se auto-oculta en nativo (no hay tracking que consentir);
            ver el propio componente. */}
        <CookieBanner />
        {/* Versión mínima de la app: tapa todo (también el splash) si el servidor pide
            actualizar. No-op mientras no llegue un 426. */}
        <UpdateRequiredScreen />
      </MotionConfig>
    </LazyMotion>
  )
}
